import React, { useState, useEffect, useCallback } from 'react';
import { Input, Empty, Spin, Typography, Tooltip, Modal, Popconfirm, Button, message as antMessage } from 'antd';
import {
  AppstoreOutlined,
  RightOutlined,
  StarFilled,
  StarOutlined,
  SearchOutlined,
  UserOutlined,
  GlobalOutlined,
  DeleteOutlined,
  ShareAltOutlined,
  TableOutlined,
  BarChartOutlined,
  CopyOutlined,
  PushpinFilled,
  PushpinOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  getMyDashboards,
  getFavoriteDashboards,
  getSharedDashboards,
  getTemplateDashboards,
  getGlobalDashboards,
  getFrontPageDashboards,
  toggleFavorite,
  updateDashboard,
  deleteDashboard,
  cloneDashboard,
} from '../services/dashboardService';
import { useAuth } from '../contexts/AuthContext';
import { useDashboard } from '../contexts/DashboardContext';
import DashboardThumbnail from './DashboardThumbnail';
import './dashboard-tokens.css';

const { Text } = Typography;

const TABS = [
  { key: 'my', label: 'My' },
  { key: 'favorites', label: 'Favorites' },
  { key: 'shared', label: 'Shared with Me' },
  { key: 'global', label: 'Global' },
];

/**
 * DashboardHomepage — Word 365-style homepage.
 *
 * Layout:
 *  1) Header:  icon + "Select a dashboard to view"
 *  2) Templates: horizontal row of A4-ratio cards (top 3) + "More templates >"
 *  3) Tab section: My | Favorites | Shared with Me  [Search]
 *     List of dashboards with name, date, description
 */
export default function DashboardHomepage({ onSelectDashboard, onDashboardDeleted, onShareDashboard, onCreateDashboard }) {
  const { isAdmin } = useAuth();
  const { refreshGlobalDashboards } = useDashboard();
  const [frontPageDashboards, setFrontPageDashboards] = useState([]);
  const [allGlobalDashboards, setAllGlobalDashboards] = useState([]);
  const [dashboards, setDashboards] = useState([]);
  const [activeTab, setActiveTab] = useState('my');
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);

  const reloadFrontPage = useCallback(() => {
    getFrontPageDashboards().then(setFrontPageDashboards).catch(() => setFrontPageDashboards([]));
  }, []);

  const reloadAllGlobal = useCallback(() => {
    getTemplateDashboards().then(setAllGlobalDashboards).catch(() => setAllGlobalDashboards([]));
  }, []);

  // Load front page dashboards once
  useEffect(() => {
    reloadFrontPage();
    reloadAllGlobal();
  }, [reloadFrontPage, reloadAllGlobal]);

  // Load dashboards when tab changes
  const loadTab = useCallback(async (tab) => {
    setLoading(true);
    try {
      let data = [];
      switch (tab) {
        case 'my':       data = await getMyDashboards(); break;
        case 'favorites': data = await getFavoriteDashboards(); break;
        case 'shared':    data = await getSharedDashboards(); break;
        case 'global':    data = await getGlobalDashboards(); break;
        default: break;
      }
      setDashboards(data);
    } catch {
      setDashboards([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTab(activeTab);
  }, [activeTab, loadTab]);

  const handleToggleFavorite = async (e, d) => {
    e.stopPropagation();
    try {
      await toggleFavorite(d.id);
      loadTab(activeTab);
    } catch { antMessage.error('Failed to toggle favorite'); }
  };

  const handlePromoteGlobal = async (e, d) => {
    e.stopPropagation();
    try {
      const newGlobal = !(d.is_global || d.is_template);
      await updateDashboard(d.id, { is_template: newGlobal, is_global: newGlobal });
      antMessage.success((d.is_global || d.is_template) ? 'Removed from Global Dashboards' : 'Made Global');
      loadTab(activeTab);
      reloadFrontPage();
      reloadAllGlobal();
      refreshGlobalDashboards?.();
    } catch (err) { antMessage.error(err.message || 'Failed to update'); }
  };

  const handleDelete = async (d) => {
    try {
      await deleteDashboard(d.id);
      antMessage.success('Dashboard deleted');
      loadTab(activeTab);
      if (onDashboardDeleted) onDashboardDeleted(d.id);
    } catch { antMessage.error('Failed to delete'); }
  };

  const handleShare = (e, d) => {
    e.stopPropagation();
    if (onShareDashboard) onShareDashboard(d);
  };

  const handleClone = async (e, d) => {
    e.stopPropagation();
    try {
      const cloned = await cloneDashboard(d.id);
      antMessage.success(`Cloned as "${cloned.name}"`);
      loadTab(activeTab);
      if (onSelectDashboard) onSelectDashboard(cloned.id);
    } catch (err) {
      antMessage.error(err.message || 'Failed to clone');
    }
  };

  const handleToggleGlobalFrontPage = async (e, d) => {
    e.stopPropagation();
    try {
      await updateDashboard(d.id, { show_on_front_page: !d.show_on_front_page });
      antMessage.success(d.show_on_front_page ? 'Removed from global front page' : 'Pinned to global front page');
      reloadFrontPage();
      reloadAllGlobal();
    } catch (err) { antMessage.error(err.message || 'Failed to update'); }
  };

  const handleBulkPin = async (pin) => {
    const targets = (allGlobalDashboards || []).filter(d => !!d.show_on_front_page !== pin);
    if (targets.length === 0) {
      antMessage.info(pin ? 'All templates are already pinned' : 'No templates are pinned');
      return;
    }
    try {
      await Promise.all(targets.map(d => updateDashboard(d.id, { show_on_front_page: pin })));
      antMessage.success(pin
        ? `Pinned ${targets.length} dashboard${targets.length === 1 ? '' : 's'} to the front page`
        : `Unpinned ${targets.length} dashboard${targets.length === 1 ? '' : 's'} from the front page`);
      reloadFrontPage();
      reloadAllGlobal();
    } catch (err) { antMessage.error(err.message || 'Bulk update failed'); }
  };

  const filtered = dashboards.filter(d => {
    if (!searchText) return true;
    const s = searchText.toLowerCase();
    return (
      d.name.toLowerCase().includes(s) ||
      (d.description || '').toLowerCase().includes(s) ||
      (d.owner_name || '').toLowerCase().includes(s)
    );
  });

  const visibleFrontPage = frontPageDashboards.slice(0, 6);

  return (
    <div className="dhp">
      {/* ── Top half: Front page dashboards (admin-pinned global) ── */}
      <div className="dhp-half dhp-half-top">
        <div className="dhp-half-inner">
          <div className="dhp-section-header">
            <div className="dhp-header">
              <AppstoreOutlined className="dhp-header-icon" />
              <span className="dhp-header-text">Select a dashboard to view</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div className="dhp-templates-more" onClick={() => setTemplateDialogOpen(true)}>
                See more <RightOutlined style={{ fontSize: 11 }} />
              </div>
            </div>
          </div>

          {visibleFrontPage.length > 0 ? (
            <div className="dhp-templates-grid">
              {visibleFrontPage.map(t => (
                <TemplateCard
                  key={t.id}
                  template={t}
                  onClick={() => onSelectDashboard(t.id, t)}
                />
              ))}
            </div>
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No dashboards pinned to the front page yet. Ask an admin to pin global dashboards."
              style={{ padding: '24px 0' }}
            />
          )}
        </div>
      </div>

      {/* ── Bottom half: Dashboard list ────────────────── */}
      <div className="dhp-half dhp-half-bottom">
        <div className="dhp-half-inner">
          {/* Tab bar + search */}
          <div className="dhp-tab-bar">
            <div className="dhp-tabs">
              {TABS.map(tab => (
                <div
                  key={tab.key}
                  className={`dhp-tab${activeTab === tab.key ? ' dhp-tab-active' : ''}`}
                  onClick={() => { setActiveTab(tab.key); setSearchText(''); }}
                >
                  {tab.label}
                </div>
              ))}
            </div>
            <div className="dhp-search" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Input
                prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                placeholder="Search"
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                allowClear
                size="small"
                style={{ width: 220 }}
              />
              {onCreateDashboard && (
                <Button
                  type="primary"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={onCreateDashboard}
                  className="dhp-new-dashboard-btn"
                >
                  New Dashboard
                </Button>
              )}
            </div>
          </div>

          {/* Column header */}
          <div className="dhp-list-header">
            <span className="dhp-list-col-name">Name</span>
            <span className="dhp-list-col-date">Date modified</span>
          </div>

          {/* Dashboard list */}
          <div className="dhp-list-body">
            {loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
                <Spin />
              </div>
            ) : filtered.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  searchText
                    ? 'No dashboards match your search'
                    : activeTab === 'shared'
                    ? 'No dashboards have been shared with you'
                    : activeTab === 'favorites'
                    ? 'No favorite dashboards yet'
                    : 'No dashboards yet'
                }
                style={{ padding: '32px 0' }}
              />
            ) : (
              filtered.map(d => (
                <DashboardRow
                  key={d.id}
                  dashboard={d}
                  activeTab={activeTab}
                  onClick={() => onSelectDashboard(d.id, d)}
                  onToggleFavorite={handleToggleFavorite}
                  onPromoteGlobal={handlePromoteGlobal}
                  onDelete={handleDelete}
                  onShare={handleShare}
                  onClone={handleClone}
                  isAdmin={isAdmin}
                />
              ))
            )}
          </div>
        </div>
      </div>

      {/* Global dashboards dialog */}
      <GlobalDashboardDialog
        open={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
        templates={allGlobalDashboards}
        onSelect={(id) => { setTemplateDialogOpen(false); onSelectDashboard(id); }}
        isAdmin={isAdmin}
        onToggleGlobalFrontPage={handleToggleGlobalFrontPage}
        onPinAll={() => handleBulkPin(true)}
        onUnpinAll={() => handleBulkPin(false)}
      />
    </div>
  );
}


/* ── Template card (horizontal rectangle with item titles) ──────────── */

function TemplateCard({ template, onClick }) {
  // Synthesize an items array for the thumbnail. The real items aren't on
  // the listing payload; we have type counts, which is enough for a hint.
  const fakeItems = [];
  for (let i = 0; i < (template.chart_count || 0); i++) fakeItems.push({ item_type: 'chart' });
  for (let i = 0; i < (template.table_count || 0); i++) fakeItems.push({ item_type: 'table' });
  for (let i = 0; i < (template.metric_count || 0); i++) fakeItems.push({ item_type: 'metric' });
  return (
    <div className="dhp-template-card" onClick={onClick}>
      <DashboardThumbnail items={fakeItems} seed={template.id ?? template.uuid ?? template.name} />
      <div className="dhp-template-card-title">{template.name}</div>
      <div className="dhp-template-card-desc">
        {template.description || 'No description'}
      </div>
      <div className="dhp-template-card-stats">
        <span><TableOutlined style={{ marginRight: 4 }} />{template.table_count || 0} tables</span>
        <span><BarChartOutlined style={{ marginRight: 4 }} />{template.chart_count || 0} charts</span>
      </div>
    </div>
  );
}


/* ── Dashboard list row ─────────────────────────────────────────────── */

function DashboardRow({ dashboard: d, activeTab, onClick, onToggleFavorite, onPromoteGlobal, onDelete, onShare, onClone, isAdmin }) {
  const dateStr = d.updated_at
    ? new Date(d.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : d.created_at
    ? new Date(d.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : '';

  const canManage = d.user_permission === 'owner' || isAdmin;
  const isGlobal = d.is_global || d.is_template;

  return (
    <div className="dhp-list-row" onClick={onClick}>
      <div className="dhp-list-row-icon">
        <AppstoreOutlined style={{ fontSize: 18, color: 'var(--theme-primary)' }} />
      </div>
      <div className="dhp-list-row-info">
        <div className="dhp-list-row-name">
          {d.name}
          {d.is_favorite && <StarFilled style={{ color: '#faad14', fontSize: 11, marginLeft: 6 }} />}
          {isGlobal && <GlobalOutlined style={{ color: '#1677ff', fontSize: 11, marginLeft: 6 }} title="Global" />}
        </div>
        <div className="dhp-list-row-desc">
          {d.description || (
            <>
              {d.item_count} item{d.item_count !== 1 ? 's' : ''}
              {activeTab === 'shared' && d.owner_name && (
                <span style={{ marginLeft: 8 }}>
                  <UserOutlined style={{ marginRight: 3, fontSize: 10 }} />
                  {d.owner_name}
                </span>
              )}
              {d.user_permission && d.user_permission !== 'owner' && (
                <span style={{ marginLeft: 8, color: d.user_permission === 'edit' ? '#1677ff' : '#8c8c8c' }}>
                  · {d.user_permission === 'edit' ? 'Edit' : 'Read-only'}
                </span>
              )}
            </>
          )}
        </div>
      </div>
      <div className="dhp-list-row-actions">
        <Tooltip title={d.is_favorite ? 'Remove from favorites' : 'Add to favorites'}>
          <span className="dhp-action-icon" onClick={(e) => onToggleFavorite(e, d)}>
            {d.is_favorite
              ? <StarFilled style={{ color: '#faad14' }} />
              : <StarOutlined />}
          </span>
        </Tooltip>
        <Tooltip title="Clone">
          <span className="dhp-action-icon" onClick={(e) => onClone(e, d)}>
            <CopyOutlined />
          </span>
        </Tooltip>
        {canManage && (
          <Tooltip title="Share">
            <span className="dhp-action-icon" onClick={(e) => onShare(e, d)}>
              <ShareAltOutlined />
            </span>
          </Tooltip>
        )}
        {isAdmin && (
          <Tooltip title={isGlobal ? 'Remove from Global' : 'Make Global'}>
            <span className="dhp-action-icon" onClick={(e) => onPromoteGlobal(e, d)}>
              <GlobalOutlined style={isGlobal ? { color: 'var(--theme-primary)' } : undefined} />
            </span>
          </Tooltip>
        )}
        {canManage && (
          <Popconfirm
            title="Delete this dashboard?"
            description="All items will be permanently removed."
            onConfirm={(e) => { if (e) e.stopPropagation(); onDelete(d); }}
            onCancel={(e) => { if (e) e.stopPropagation(); }}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <span className="dhp-action-icon" onClick={(e) => e.stopPropagation()} style={{ color: '#ff4d4f' }}>
              <DeleteOutlined />
            </span>
          </Popconfirm>
        )}
      </div>
      <div className="dhp-list-row-date">{dateStr}</div>
    </div>
  );
}


/* ── Global dashboard dialog (full list with admin pin-to-global) ──── */

function GlobalDashboardDialog({ open, onClose, templates, onSelect, isAdmin, onToggleGlobalFrontPage, onPinAll, onUnpinAll }) {
  const [query, setQuery] = React.useState('');
  const pinnedCount = (templates || []).filter(d => d.show_on_front_page).length;
  const totalCount = (templates || []).length;
  const q = query.trim().toLowerCase();
  const filtered = q
    ? (templates || []).filter(t => (t.name || '').toLowerCase().includes(q) || (t.description || '').toLowerCase().includes(q))
    : (templates || []);
  return (
    <Modal
      title="Dashboard Templates"
      open={open}
      onCancel={onClose}
      footer={null}
      width={1530}
      destroyOnClose
      centered
      styles={{ body: { height: 860, overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: '0px 20px 0px' } }}
      className="dhp-templates-dialog"
    >
      {(!templates || templates.length === 0) ? (
        <Empty description="No Global Dashboards available" />
      ) : (
        <div className="dhp-templates-dialog-tab">
          <div className="dhp-templates-dialog-toolbar">
            <Input
              allowClear
              placeholder="Search dashboards"
              prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              style={{ width: 280 }}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span className="dhp-templates-dialog-count">
                {pinnedCount} of {totalCount} pinned to front page
              </span>
              {isAdmin && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button size="small" icon={<PushpinFilled />} onClick={onPinAll} disabled={pinnedCount === totalCount}>
                    Pin All
                  </Button>
                  <Button size="small" icon={<PushpinOutlined />} onClick={onUnpinAll} disabled={pinnedCount === 0}>
                    Un-pin All
                  </Button>
                </div>
              )}
            </div>
          </div>
          <div className="dhp-templates-dialog-grid">
            {filtered.length === 0 ? (
              <Empty description="No matching dashboards" style={{ gridColumn: '1 / -1', marginTop: 32 }} />
            ) : filtered.map(t => (
              <div key={t.id} className="dhp-templates-dialog-cell">
                <TemplateCard template={t} onClick={() => onSelect(t.id)} />
                {isAdmin && (
                  <Tooltip title={t.show_on_front_page ? 'Unpin from global front page' : 'Pin to global front page'}>
                    <Button
                      type="text"
                      size="small"
                      icon={t.show_on_front_page ? <PushpinFilled style={{ color: '#1677ff' }} /> : <PushpinOutlined />}
                      style={{ position: 'absolute', top: 4, right: 4, zIndex: 2, background: 'rgba(255,255,255,0.85)', borderRadius: 4 }}
                      onClick={(e) => { e.stopPropagation(); onToggleGlobalFrontPage(e, t); }}
                    />
                  </Tooltip>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
}
